import { NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';
import { FrontendLoggerService } from './frontend-logger.service';
import type { Notification } from './notification.service';
import type { NotificationReceiver } from './notification-state.store';

export interface NotificationWsTicketResponse {
  wsUrl: string;
  ticket: string;
  expiresAt?: string;
}

export class NotificationWebSocketClient {
  private socket: WebSocket | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly reconnectInterval = 10_000;
  private reconnectAttempts = 0;

  constructor(
    private readonly apiService: ApiService,
    private readonly ngZone: NgZone,
    private readonly logger: FrontendLoggerService,
    private readonly receiver: NotificationReceiver
  ) {}

  connect(): void {
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)
    ) {
      this.logger.warn('[NotificationService] WS already OPEN/CONNECTING, skipping new connect.');
      return;
    }

    this.getTicket().pipe(take(1)).subscribe({
      next: (response) => this.openSocket(response),
      error: (error) => {
        this.logger.error(`[NotificationService] Failed to obtain WS ticket: ${String(error)}`);
      },
    });
  }

  getTicket(): Observable<NotificationWsTicketResponse> {
    return this.apiService.post<NotificationWsTicketResponse>('notifications/ws-ticket', {});
  }

  buildSocketUrl(wsUrl: string, ticket: string): string | null {
    try {
      const url = this.resolveWsUrl(wsUrl);
      if (url.protocol === 'http:') url.protocol = 'ws:';
      if (url.protocol === 'https:') url.protocol = 'wss:';
      url.searchParams.set('ticket', ticket);
      return url.toString();
    } catch (error) {
      this.logger.error(
        `[NotificationService] Invalid wsUrl from ticket response: ${String(error)}`
      );
      return null;
    }
  }

  resolveWsUrl(wsUrl: string): URL {
    if (/^wss?:\/\//i.test(wsUrl) || /^https?:\/\//i.test(wsUrl)) {
      return new URL(wsUrl);
    }
    const base = this.getWsBaseUrl();
    return new URL(wsUrl, base.endsWith('/') ? base : `${base}/`);
  }

  getWsBaseUrl(): string {
    const configuredWsUrl = (environment.wsUrl || '').trim();
    if (configuredWsUrl) return configuredWsUrl;

    const apiUrl = (environment.apiUrl || '').trim();
    if (!apiUrl) return globalThis.location?.origin ?? '';

    const apiUrlObject = new URL(apiUrl);
    apiUrlObject.protocol = apiUrlObject.protocol === 'https:' ? 'wss:' : 'ws:';
    return apiUrlObject.toString();
  }

  disconnect(): void {
    try {
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
      if (this.socket) {
        try {
          this.socket.close(1000, 'Client disconnect');
        } catch {
          // Closing an already closed browser socket is harmless.
        }
        this.socket = null;
      }
      this.reconnectAttempts = 0;
      this.logger.info(`WS disconnected (client): ${new Date().toISOString()}`);
    } catch (error) {
      this.logger.error(`Error during NotificationService.disconnect: ${JSON.stringify(error)}`);
    }
  }

  private openSocket(response: NotificationWsTicketResponse): void {
    const wsUrl = (response?.wsUrl || '').trim();
    const ticket = (response?.ticket || '').trim();
    if (!wsUrl || !ticket) {
      this.logger.error('[NotificationService] WS ticket response missing wsUrl or ticket.');
      return;
    }

    const finalUrl = this.buildSocketUrl(wsUrl, ticket);
    if (!finalUrl) return;

    try {
      this.socket = new globalThis.WebSocket(finalUrl);
    } catch (error) {
      this.logger.error(`[NotificationService] Error creating WebSocket: ${String(error)}`);
      return;
    }

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.logger.info('[NotificationService] WS connected.');
    };
    this.socket.onmessage = (event) => this.handleMessage(event.data);
    this.socket.onclose = (event) => this.scheduleReconnect(event);
    this.socket.onerror = (event) => {
      this.logger.error(`[NotificationService] WS error: ${JSON.stringify(event)}`);
      try {
        this.socket?.close();
      } catch {
        // The close handler performs recovery when the browser permits it.
      }
    };
  }

  private handleMessage(raw: unknown): void {
    this.ngZone.run(() => this.receiver.receive(this.parseNotification(raw)));
  }

  private parseNotification(raw: unknown): Notification {
    let parsed: any = null;
    try {
      parsed = JSON.parse(String(raw));
    } catch {
      parsed = null;
    }

    if (!parsed || typeof parsed !== 'object') {
      return this.createNotification({ message: String(raw) });
    }
    return this.createNotification({
      id: parsed.id && String(parsed.id),
      type: parsed.type,
      message: parsed.message || String(parsed),
      priority: parsed.priority,
      timestamp: parsed.timestamp ? new Date(parsed.timestamp) : undefined,
      data: parsed.data && typeof parsed.data === 'object' ? parsed.data : undefined,
    });
  }

  private createNotification(input: Partial<Notification>): Notification {
    return {
      id: input.id || Math.random().toString(36).slice(2, 11),
      type: input.type || 'info',
      message: input.message || '',
      priority: input.priority || 'medium',
      timestamp: input.timestamp || new Date(),
      read: false,
      ...(input.data ? { data: input.data } : {}),
    };
  }

  private scheduleReconnect(event: CloseEvent): void {
    const diagnostic = {
      code: event?.code || 'n/a',
      reason: event?.reason || '',
      wasClean: event?.wasClean || false,
    };
    this.logger.error(`[NotificationService] WS closed: ${JSON.stringify(diagnostic)}`);
    this.reconnectAttempts += 1;
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
    this.logger.info(
      `[NotificationService] Attempting WS reconnect, attempt #${this.reconnectAttempts}, waiting ${this.reconnectInterval} ms`
    );
    this.reconnectTimeout = setTimeout(() => this.connect(), this.reconnectInterval);
  }
}
